import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import io from "socket.io-client";
import { FaClock, FaStar, FaChartBar } from "react-icons/fa";
import { SkeletonLobby } from "../../Skeleton";
import API_URL from '../../../utils/api';
import "./PostBreakoutEvaluation.css";
export default function PostBreakoutEvaluation() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [partner, setPartner] = useState(null);
  const [userId] = useState(() => sessionStorage.getItem("userId") || localStorage.getItem("userId"));
  const [responses, setResponses] = useState({
    actualEnjoyment: 5,
    actualConnection: 5,
    actualComfort: 5,
    actualInterest: 5,
    actualOpenness: 5
  });
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [partnerSubmitted, setPartnerSubmitted] = useState(false);
  const [discrepancies, setDiscrepancies] = useState(null);
  const [socket, setSocket] = useState(null);
  const [pairKey, setPairKey] = useState(null);
  
  // Helper to create consistent pair key
  const getPairKey = (userId1, userId2) => {
    const ids = [String(userId1), String(userId2)].sort();
    return ids.join('-');
  };
  // Fetch session and determine partner
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await fetch(`${API_URL}/api/sessions/${sessionId}`);
        const data = await response.json();
        if (data.members && typeof data.members === 'string') {
          data.members = JSON.parse(data.members);
        }
        setSession(data);
        // Get pairing information
        const pairResponse = await fetch(`${API_URL}/api/sessions/${sessionId}/start-breakout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" }
        });
        const pairData = await pairResponse.json();
        if (pairData.ok && pairData.pairs) {
          const myPair = pairData.pairs.find(p => 
            p.some(member => String(member.user_id || member.userId) === String(userId))
          );
          if (myPair) {
            const partnerMember = myPair.find(member => 
              String(member.user_id || member.userId) !== String(userId)
            );
            setPartner(partnerMember);
            // Set pair key for socket communication
            if (partnerMember) {
              const partnerId = partnerMember.user_id || partnerMember.userId;
              setPairKey(getPairKey(userId, partnerId));
            }
          }
        }
      } catch (error) {
        }
    };
    fetchSession();
  }, [sessionId, userId]);
  // Socket.io connection for real-time updates
  useEffect(() => {
    if (!partner) return; // Wait for partner data
    const newSocket = io(API_URL);
    setSocket(newSocket);
    newSocket.on('connect', () => {
      newSocket.emit("join-session", sessionId);
      });

    const currentPairKey = pairKey;
    const partnerId = partner?.user_id || partner?.userId;
    
    // Listen for partner submission - check if it's from our pair
    newSocket.on("post-eval-submitted", (data) => {
      if (!currentPairKey) return;
      if (data.pairKey !== currentPairKey) return;
      const partnerIdStr = String(partnerId || '');
      const submittedUserIdStr = String(data.userId || '');
      if (submittedUserIdStr === partnerIdStr) {
        setPartnerSubmitted(true);
      }
    });
    
    // Listen for pair-ready signal - check if it's our pair
    newSocket.on("post-eval-pair-ready", (data) => {
      // Check if this pair-ready is for our pair
      if (currentPairKey && data.pairKey === currentPairKey) {
        navigate(`/coaching/${sessionId}`);
      }
      // Also check using userIds array
      const userIdStr = String(userId);
      const partnerIdStr = String(partnerId || '');
      if (data.userIds && (data.userIds.includes(userIdStr) || data.userIds.includes(partnerIdStr))) {
        navigate(`/coaching/${sessionId}`);
      }
    });
    
    // Keep backward compatibility with old event (for 2-person sessions)
    newSocket.on("post-eval-both-ready", () => {
      navigate(`/coaching/${sessionId}`);
    });
    return () => {
      newSocket.disconnect();
    };
  }, [sessionId, partner, pairKey, navigate, userId]);
  const handleSliderChange = (field, value) => {
    setResponses(prev => ({ ...prev, [field]: parseInt(value) }));
  };
  const handleSubmit = async () => {
    try {
      // Save post-breakout evaluation
      const result = await fetch(`${API_URL}/api/evaluations/post-breakout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          userId,
          partnerId: partner?.user_id || partner?.userId,
          responses,
          timestamp: new Date().toISOString()
        })
      });
      const data = await result.json();
      // Store discrepancies if available
      if (data.discrepancies) {
        setDiscrepancies(data.discrepancies);
      }
      // Mark as submitted
      setHasSubmitted(true);
      // Notify partner via socket
      if (socket) {
        socket.emit("post-eval-submit", {
          sessionId,
          userId: String(userId),
          partnerId: String(partner?.user_id || partner?.userId)
        });
      }
      // If partner already submitted, navigate to coaching after short delay
      if (partnerSubmitted) {
        setTimeout(() => {
          navigate(`/coaching/${sessionId}`);
        }, 3000);
      }
    } catch (error) {
      }
  };
  if (!session || !partner) {
    return (
      <div className="post-eval-root">
        <SkeletonLobby />
      </div>
    );
  }
  const questions = [
    {
      field: 'actualEnjoyment',
      label: 'How much did you enjoy this conversation?',
      lowLabel: 'Not at all',
      highLabel: 'Very much'
    },
    {
      field: 'actualConnection',
      label: 'How connected did you feel with your partner?',
      lowLabel: 'Not connected',
      highLabel: 'Very connected'
    },
    {
      field: 'actualComfort',
      label: 'How comfortable did you feel during this conversation?',
      lowLabel: 'Very uncomfortable',
      highLabel: 'Very comfortable'
    },
    {
      field: 'actualInterest',
      label: 'How interesting was this conversation?',
      lowLabel: 'Not interesting',
      highLabel: 'Very interesting'
    },
    {
      field: 'actualOpenness',
      label: 'How open were you in sharing with your partner?',
      lowLabel: 'Not open',
      highLabel: 'Very open'
    }
  ];
  return (
    <div className="post-eval-root">
      {/* Header */}
      <div className="post-eval-header">
        <h2>After Your Breakout Session</h2>
        <p className="post-eval-subtitle">Affective Experience Assessment</p>
      </div>
      {/* Partner Info */}
      <div className="post-eval-partner-card">
        <div className="partner-info-label">You just spoke with:</div>
        <div className="partner-info-display">
          <div className="partner-avatar-large">
            {partner.name?.charAt(0).toUpperCase()}
          </div>
          <div className="partner-name-large">{partner.name}</div>
        </div>
      </div>
      {/* Instructions */}
      <div className="post-eval-instructions">
        <div className="instruction-icon"><FaStar /></div>
        <div className="instruction-text">
          Now that your conversation has ended, please reflect on your actual experience. 
          Your honest responses help us understand the accuracy of affective forecasting.
        </div>
      </div>
      {/* Questions */}
      <div className="post-eval-questions">
        {questions.map((q, index) => (
          <div key={q.field} className="eval-question-card">
            <div className="question-number">{index + 1}</div>
            <div className="question-content">
              <div className="question-label">{q.label}</div>
              <div className="slider-container">
                <div className="slider-labels">
                  <span className="slider-label-low">{q.lowLabel}</span>
                  <span className="slider-value">{responses[q.field]}</span>
                  <span className="slider-label-high">{q.highLabel}</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={responses[q.field]}
                  onChange={(e) => handleSliderChange(q.field, e.target.value)}
                  className="eval-slider"
                />
                <div className="slider-scale">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                    <span key={num} className="scale-marker">{num}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Submit Button */}
      <div className="post-eval-submit-section">
        {!hasSubmitted ? (
          <>
            <button 
              className="post-eval-submit-btn"
              onClick={handleSubmit}
            >
              Complete Evaluation →
            </button>
            <div className="submit-note">
              Your responses will be compared with your predictions to measure forecasting accuracy
            </div>
          </>
        ) : (
          <div className="post-eval-waiting">
            {discrepancies && (
              <div className="discrepancy-summary">
                <div className="discrepancy-icon"><FaChartBar /></div>
                <div className="discrepancy-text">
                  Evaluation complete! Your forecasting data has been saved.
                </div>
              </div>
            )}
            <div className="waiting-spinner"><FaClock /></div>
            <div className="waiting-text">
              {partnerSubmitted ? (
                <>Redirecting to dashboard...</>
              ) : (
                <>Waiting for <strong>{partner?.name}</strong> to complete their evaluation...</>
              )}
            </div>
            <div className="waiting-subtext">
              You'll be redirected automatically
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
